import PropTypes from 'prop-types';
import { Text, Link } from '@nerdwallet/currency';
import Image from '@nerdwallet/react-image';
import {
  setTrk,
  TrkTag,
  TrkElement,
} from '@nerdwallet/nw-client-lib/trk-helper';
import UniversalContainer, {
  universalContainerDefaultProps,
  universalContainerPropTypes,
} from '../universal-container';
import {
  boolCheckboxSpec,
  repeatable,
  stringInputSpec,
} from '../../lib/editing-configs';
import UniversalText from '../universal-text/universal-text';
import styles from './awards-2024-buttons.module.less';

function getLinkUrl(linkUrl, shouldTrack, title) {
  const isAnchorLink = linkUrl.charAt(0) === '#';
  if (isAnchorLink || !shouldTrack) {
    return linkUrl;
  }

  const modifiedUrl = setTrk({
    url: linkUrl,
    params: {
      [TrkTag.location]: 'AwardsNavigation',
      [TrkTag.element]: TrkElement.hyperlink,
      [TrkTag.copy]: title?.toLowerCase(),
      [TrkTag.sectionCategory]: 'AwardsNavigation--header',
    },
  });

  return modifiedUrl;
}

const Awards2024ButtonsBox = ({
  boxIconUrl,
  boxTitle,
  boxLinkUrl,
  shouldTrack,
}) => {
  const url = getLinkUrl(boxLinkUrl, shouldTrack, boxTitle);

  return (
    <Link href={url} className={styles.boxLink} data-testid="button-box-link">
      <div className={styles.boxItem}>
        {boxIconUrl && (
          <Image
            className={styles.boxIcon}
            src={boxIconUrl}
            alt="box-icon"
            backgroundColor="transparent"
          />
        )}
        <div className={styles.boxTitle}>
          <Text>{boxTitle}</Text>
        </div>
      </div>
    </Link>
  );
};

Awards2024ButtonsBox.propTypes = {
  children: PropTypes.node,
  boxIconUrl: PropTypes.string,
  boxTitle: PropTypes.string,
  boxLinkUrl: PropTypes.string,
  shouldTrack: PropTypes.bool,
};

Awards2024ButtonsBox.defaultProps = {
  children: null,
  boxIconUrl: '',
  boxTitle: '',
  boxLinkUrl: '',
  shouldTrack: false,
};

const Awards2024Buttons = ({ container, title, buttons, shouldTrack }) => {
  return (
    <UniversalContainer
      {...container}
      data-testid="awards2024-buttons-container"
    >
      <UniversalText {...title} />
      <div className={styles.boxItemsContainer}>
        {buttons?.map((box) => (
          <Awards2024ButtonsBox
            key={box.$unique_key}
            boxIconUrl={box.boxIconUrl}
            boxLinkUrl={box.boxLinkUrl}
            boxTitle={box.boxTitle}
            shouldTrack={shouldTrack}
          />
        ))}
      </div>
    </UniversalContainer>
  );
};

Awards2024Buttons.displayName = 'Awards2024Buttons';

Awards2024Buttons.propTypes = {
  children: PropTypes.node,
  container: universalContainerPropTypes,
  title: UniversalText.propTypes,
  buttons: PropTypes.array,
  shouldTrack: PropTypes.bool,
};

Awards2024Buttons.defaultProps = {
  children: null,
  container: universalContainerDefaultProps,
  title: 'Awards2024Buttons',
  buttons: [],
  shouldTrack: false,
};

Awards2024Buttons.getEditingConfig = (componentDocument) => {
  return [
    ...UniversalContainer.getEditingConfig(
      componentDocument,
      'Container',
      ['padding', 'margin', 'backgroundColor', 'backgroundImage', 'jumpLinkId'],
      `container`
    ),
    ...UniversalText.getEditingConfig(
      componentDocument,
      'Title',
      [
        'fontWeight',
        'groupButton',
        'margin',
        'padding',
        'padding',
        'textChronicle',
        'textColor',
        'textSize',
        'textType',
      ],
      'title'
    ),
    boolCheckboxSpec(
      'shouldTrack',
      'Add BOA Awards Analytics Instrumentation?'
    ),
    repeatable('buttons', 'Buttons', 'Button', () => [
      stringInputSpec('boxTitle', 'Button Title'),
      stringInputSpec('boxIconUrl', 'Button Icon URL'),
      stringInputSpec('boxLinkUrl', 'Button Link URL'),
    ]),
  ];
};
export default Awards2024Buttons;
